import { Container } from '../container/container';
import { routes } from '../../taskpane/data/constants/routes';
import { getUserEmail } from './getUserEmail';

export interface ProbeResult {
  reachable: boolean; // got ANY http response → firewall not blocking
  status: number | null;
  durationMs: number;
  error: string | null; // axios err.code ?? message on transport failure
}

export interface NetworkProbeResult {
  inbound: ProbeResult;
  zim: ProbeResult;
}

const PROBE_TIMEOUT_MS = 3000;

// 4xx/5xx must NOT throw → still reachable
const probeRequestConfig = { timeout: PROBE_TIMEOUT_MS, validateStatus: () => true };

const unreachable = (error: string, durationMs = 0): ProbeResult => ({
  reachable: false,
  status: null,
  durationMs,
  error,
});

const runProbe = async (request: () => Promise<{ status: number }>): Promise<ProbeResult> => {
  const start = performance.now();
  const elapsed = () => Math.round(performance.now() - start);

  try {
    const res = await request();
    return { reachable: true, status: res.status, durationMs: elapsed(), error: null };
  } catch (e) {
    const err = e as { code?: string; message?: string };
    return unreachable(err.code ?? err.message ?? 'unknown error', elapsed());
  }
};

const probeInbound = (): Promise<ProbeResult> => {
  const client = Container.phishedClient;
  if (!client) return Promise.resolve(unreachable('phishedClient not initialised'));

  return runProbe(() =>
    client.post(routes.phished.configuration, { email: getUserEmail() }, probeRequestConfig),
  );
};

const probeZim = (): Promise<ProbeResult> => {
  const client = Container.zimHealthClient;
  if (!client) return Promise.resolve(unreachable('zimHealthClient not initialised'));

  return runProbe(() => client.get(routes.zim.health, probeRequestConfig));
};

export const probeNetwork = async (): Promise<NetworkProbeResult> => {
  const [inbound, zim] = await Promise.all([probeInbound(), probeZim()]);

  return { inbound, zim };
};
